<template>
    <DCard
        :title="__('customers.business_incidents.connected_tickets.title', {numberOfItems: items.length})"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <DSwitch
                color="success"
                variant="3d"
                :disabled="isInLoadingState"
                :checked="showCardData"
                @update:checked="showCardSection"
            />
        </template>
        <DRow v-if="showCardData">
            <DCol col="8">
                <section class="tickets-table">
                    <div class="table-header">
                        <div>{{ __('customers.business_incidents.connected_tickets.ticket_id') }}</div>
                        <div>{{ __('customers.business_incidents.connected_tickets.subject') }}</div>
                        <div>{{ __('customers.business_incidents.connected_tickets.status') }}</div>
                        <div>{{ __('customers.business_incidents.connected_tickets.created') }}</div>
                        <div>{{ __('customers.business_incidents.connected_tickets.updated') }}</div>
                        <div>
                            <DButton
                                v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                id="add"
                                icon="plus"
                                mini
                                :disabled="isInLoadingState"
                                @click="addTickets"
                            />
                        </div>
                    </div>
                    <main>
                        <div
                            v-for="(item, index) in items"
                            :key="index"
                            class="item-group"
                        >
                            <div class="item-header">
                                <div>
                                    <a
                                        v-if="item.ticketUrl !== null"
                                        :href="item.ticketUrl"
                                        target="_blank"
                                    >
                                        {{ item.ticket_id }}
                                    </a>
                                    <span v-else>
                                        {{ item.ticket_id }}
                                    </span>
                                </div>
                                <div>
                                    {{ item.subject }}
                                </div>
                                <div>
                                    {{ item.status }}
                                </div>
                                <div>
                                    {{ setDateFormat(item.created) }}
                                </div>
                                <div>
                                    {{ setDateFormat(item.updated) }}
                                </div>
                                <div>
                                    <DButton
                                        v-if="incident.status === 'open'"
                                        :id="`delete_ticket_${item.id}`"
                                        :disabled="isInLoadingState"
                                        icon="trash-can-outline"
                                        color="danger"
                                        mini
                                        @click="showRemoveItem(item)"
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                </section>
            </DCol>
        </DRow>
        <AddItems
            v-if="showAddTicketModal"
            :show-modal="showAddTicketModal"
            :incident="incident"
            :title="__('customers.business_incidents.connected_tickets.modal.add.title')"
            :label="__('customers.business_incidents.connected_tickets.modal.add.label')"
            :section="'tickets'"
            @close-modal="closeAddModal"
            @confirm-modal="save"
        />
        <RemoveItem
            v-if="showRemoveItemModal"
            :show-modal="showRemoveItemModal"
            :title="__('customers.business_incidents.connected_tickets.modal.remove.title')"
            :message="__('customers.business_incidents.connected_tickets.modal.remove.message', {ticket: ticketId})"
            @close-modal="closeRemoveModal"
            @confirm-modal="remove"
        />
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import RemoveItem from '~@/Components/Customers/BusinessIncidents/RemoveItem'
import AddItems from '~@/Components/Customers/BusinessIncidents/AddItems'

const props = defineProps({
    items: {
        type: Array,
        default () {
            return []
        }
    },
    incident: {
        type: Object,
        default () {
            return {}
        }
    },
    showCard: {
        type: Boolean,
        default () {
            return true
        }
    }
})

const emit = defineEmits(['addTickets'])

const showCardData = ref(props.showCard)
const showRemoveItemModal = ref(false)
const showAddTicketModal = ref(false)
const ticketId = ref(null)
const businessIncidentTicketId = ref(null)
const userPermissions = computed(() => usePage().props.auth.permissions)

function showCardSection (value) {
    showCardData.value = value
}

function showRemoveItem (item) {
    businessIncidentTicketId.value = item.id
    ticketId.value = item.ticket_id
    showRemoveItemModal.value = true
}

function addTickets () {
    showAddTicketModal.value = true
}

function closeRemoveModal () {
    businessIncidentTicketId.value = null
    ticketId.value = null
    showRemoveItemModal.value = false
}

function remove () {
    router.delete(route('customers.customers.delete-business-incident-ticket', { businessIncidentTicket: businessIncidentTicketId.value }))
    showRemoveItemModal.value = false
}

function closeAddModal () {
    showAddTicketModal.value = false
}

function save (form) {
    emit('addTickets', form)

    showAddTicketModal.value = false
}

</script>

<style lang="scss" scoped>
.tickets-table {
    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 15% 20% 15% 15% 15% 20%;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 15% 20% 15% 15% 15% 20%;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
            }
        }
    }
}
</style>
